// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { ShipToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    shipTo: ShipToModel;
    pickUpWarehouse?: WarehouseModel | null;
    extendedStyles?: OrderApprovalDetailsShippingInformationStyles;
}

export interface OrderApprovalDetailsShippingInformationStyles {
    shippingInformationGridContainer?: GridContainerProps;
    shippingInformationTitleGridItem?: GridItemProps;
    shippingInformationTitle?: TypographyProps;
    shippingInformationAddressGridItem?: GridItemProps;
    shippingAddressTitle?: TypographyProps;
    shippingAddress?: AddressInfoDisplayStyles;
    shippingOtherInformationGridItem?: GridItemProps;
    shippingOtherInformationGridContainer?: GridContainerProps;
    shippingInformationCarrierGridItem?: GridItemProps;
    shippingCarrierTitle?: TypographyProps;
    shippingCarrierText?: TypographyProps;
    shippingInformationDateRequestedGridItem?: GridItemProps;
    shippingDateRequestedTitle?: TypographyProps;
    shippingDateRequestedText?: TypographyProps;
    shippingInformationServiceGridItem?: GridItemProps;
    shippingServiceTitle?: TypographyProps;
    shippingServiceText?: TypographyProps;
    // XNG-Changes:
    divider?: GridItemProps;
    informationContainer?: InjectableCss;
    displayAddressGridItem?: GridItemProps;
}

export const orderApprovalDetailsShippingInformationStyles: OrderApprovalDetailsShippingInformationStyles = {
    shippingInformationGridContainer: {
        gap: 10,
    },
    shippingInformationTitleGridItem: {
        width: 12,
    },
    shippingInformationTitle: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
            }
            padding-top: 0;
            margin-bottom: 0;
        `,
    },
    shippingInformationAddressGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    shippingAddressTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingOtherInformationGridItem: {
        width: [12, 12, 8, 8, 8],
    },
    shippingOtherInformationGridContainer: {
        gap: 10,
    },
    shippingInformationCarrierGridItem: {
        css: css`
            flex-direction: column;
            margin-bottom: 5px;
        `,
        width: 6,
    },
    shippingCarrierTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingInformationDateRequestedGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    shippingDateRequestedTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    shippingInformationServiceGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 6,
    },
    shippingServiceTitle: {
        variant: "h6",
        as: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
};

type Props = OwnProps;

const OrderApprovalDetailsShippingInformation = ({ cart, shipTo, pickUpWarehouse, extendedStyles }: Props) => {
    const [mergedStyles] = useState(() => mergeToNew(orderApprovalDetailsShippingInformationStyles, extendedStyles));

    /* XNG-Changes: */
    return (
        <GridContainer
            {...mergedStyles.shippingInformationGridContainer}
            data-test-selector="orderApprovalDetailsShippingInformation"
        >
            {/* XNG-Changes: */}
            {/* <GridItem {...mergedStyles.shippingInformationTitleGridItem}>
                <Typography {...mergedStyles.shippingInformationTitle}>{translate("Shipping Information")}</Typography>
            </GridItem> */}
            {cart.fulfillmentMethod === "Ship" && (
                <GridItem {...mergedStyles.shippingInformationAddressGridItem} data-test-selector="GridItem">
                    <Typography {...mergedStyles.shippingAddressTitle}>{translate("Shipping Information")}</Typography>
                    <GridItem {...mergedStyles.divider} />
                    <StyledWrapper {...mergedStyles.informationContainer} data-test-selector="GridContainer">
                        <GridItem {...mergedStyles.displayAddressGridItem}>
                            <AddressInfoDisplay
                                companyName={shipTo.companyName}
                                address1={shipTo.address1}
                                address2={shipTo.address2}
                                address3={shipTo.address3}
                                address4={shipTo.address4}
                                city={shipTo.city}
                                postalCode={shipTo.postalCode}
                                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                extendedStyles={mergedStyles.shippingAddress}
                            />
                        </GridItem>
                        <GridItem {...mergedStyles.shippingOtherInformationGridItem}>
                            <GridContainer {...mergedStyles.shippingOtherInformationGridContainer}>
                                {/* XNG-CHANGE : GSS-1104 */}
                                {/* {cart.fulfillmentMethod === "Ship" && cart.shipVia && cart.carrier && (
                                    <GridItem {...mergedStyles.shippingInformationCarrierGridItem}>
                                        <Typography {...mergedStyles.shippingCarrierTitle}>
                                            {translate("Carrier")}
                                        </Typography>
                                        <Typography {...mergedStyles.shippingCarrierText}>
                                            {cart.carrier!.description}
                                        </Typography>
                                    </GridItem>
                                )} */}
                                {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                                    <GridItem {...mergedStyles.shippingInformationDateRequestedGridItem}>
                                        <Typography {...mergedStyles.shippingDateRequestedTitle}>
                                            {translate("Date Requested")}
                                        </Typography>
                                        <Typography
                                            {...mergedStyles.shippingDateRequestedText}
                                            data-test-selector="orderApprovalDetails_requestedDate"
                                        >
                                            <LocalizedDateTime
                                                dateTime={
                                                    cart.requestedDeliveryDateDisplay || cart.requestedPickupDateDisplay
                                                }
                                            />
                                        </Typography>
                                    </GridItem>
                                )}
                                {/* XNG-CHANGE : GSS-1104 */}
                                {/* {cart.fulfillmentMethod === "Ship" && cart.shipVia && cart.carrier && (
                                    <GridItem {...mergedStyles.shippingInformationServiceGridItem}>
                                        <Typography {...mergedStyles.shippingServiceTitle}>
                                            {translate("Service")}
                                        </Typography>
                                        <Typography {...mergedStyles.shippingServiceText}>
                                            {cart.shipVia?.description}
                                        </Typography>
                                    </GridItem>
                                )} */}
                            </GridContainer>
                        </GridItem>
                    </StyledWrapper>
                </GridItem>
            )}
            {cart.fulfillmentMethod === "PickUp" && pickUpWarehouse && (
                <GridItem {...mergedStyles.shippingInformationAddressGridItem}>
                    <Typography {...mergedStyles.shippingAddressTitle}>{translate("Pick Up Location")}</Typography>
                    <GridItem {...mergedStyles.divider} />
                    <StyledWrapper {...mergedStyles.informationContainer} data-test-selector="GridContainer">
                        <GridItem {...mergedStyles.displayAddressGridItem}>
                            <AddressInfoDisplay
                                {...pickUpWarehouse}
                                companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                extendedStyles={mergedStyles.shippingAddress}
                            />
                        </GridItem>
                        <GridItem {...mergedStyles.shippingOtherInformationGridItem}>
                            <GridContainer {...mergedStyles.shippingOtherInformationGridContainer}>
                                {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                                    <GridItem {...mergedStyles.shippingInformationDateRequestedGridItem}>
                                        <Typography {...mergedStyles.shippingDateRequestedTitle}>
                                            {translate("Date Requested")}
                                        </Typography>
                                        <Typography
                                            {...mergedStyles.shippingDateRequestedText}
                                            data-test-selector="orderApprovalDetails_requestedDate"
                                        >
                                            <LocalizedDateTime
                                                dateTime={
                                                    cart.requestedDeliveryDateDisplay || cart.requestedPickupDateDisplay
                                                }
                                            />
                                        </Typography>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    </StyledWrapper>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default OrderApprovalDetailsShippingInformation;
